import { gql } from '@apollo/client'
import { ApolloQueryResult } from '@apollo/client/core'

import { Card, EMV, Terminal } from '../models/payment'
import {
  SaleCreateErrorCode,
  SaleCreateErrorField,
  SaleMetadata,
  ServiceFields,
} from '../models/sale.constants'

export interface GqlPaymentSurcharge {
  rule: string
  amount: string
}

export interface GqlSaleAddress {
  addressLine1: string
  addressLine2: string
  city: string
  state: string
  postalCode: string
  countryCode: string
  suburb: string
}

export interface GqlPayment {
  id: string
  registerID: string
  registerSequenceID: string
  retailerPaymentTypeID: string
  paymentTypeID?: number
  paymentDate: string
  amount: string // numeric string
  currency: string
  source?: string
  sourceID?: string
  surcharge?: GqlPaymentSurcharge
  externalAttributes?: GqlPaymentExternalAttributes
  billingAddress?: GqlSaleAddress
}

export interface GqlPaymentExternalAttributes {
  transactionID?: string
  referenceID?: string
  merchantID?: string
  authCode?: string
  signatureUrl?: string
  signatureBase64?: string
  transactionStatus?: string
  card?: Card
  emv?: EMV
  terminal?: Terminal
}

export interface GqlAdjustment {
  type: AdjustmentType
  name: string
  sourceID: string
  targetMethod: AdjustmentTargetMethod
  targetType: AdjustmentTargetType
  value: string // numeric string
  valueType: AdjustmentValueType
  targets?: string[]
}

export enum AdjustmentType {
  DISCOUNT = 'DISCOUNT',
  NON_CASH_FEE = 'NON_CASH_FEE',
  TIP = 'TIP',
  SURCHARGE = 'SURCHARGE',
}

export enum AdjustmentTargetMethod {
  EACH = 'EACH',
  PROPORTIONED = 'PROPORTIONED',
  SINGLE = 'SINGLE',
}

export enum AdjustmentTargetType {
  LINE_ITEM = 'LINE_ITEM',
  SALE = 'SALE',
}

export enum AdjustmentValueType {
  FIXED = 'FIXED',
  PERCENTAGE = 'PERCENTAGE',
}

export enum AdjustmentName {
  NON_CASH_FEE = 'Non-cash Fee',
  TIP = 'Tip',
}

export enum GqlLineItemState {
  PENDING = 'PENDING',
  RESERVED = 'RESERVED',
  CONFIRMED = 'CONFIRMED',
  COMPLETED = 'COMPLETED',
  RETURNED = 'RETURNED',
  EXCHANGED = 'EXCHANGED',
}
export enum GqlFulfilmentType {
  DISPATCH = 'DISPATCH',
  PICKUP = 'PICKUP',
}

export interface GqlCustomField {
  name: string
  integerValue?: string // numeric string
  stringValue?: string
  booleanValue?: boolean
  dateValue?: string
  productID?: string
  stringArrayValue?: string[]
}

export interface GqlLineItemPromotion {
  promotionID: string
  amount: string // numeric string
  promoCode?: string
  promoCodeID?: string
}

/**
 * Line item type used when interacting with graphql to create a sale
 */
export interface GqlLineItem {
  attributes?: Array<{ name: string; value: string }>
  customFields?: GqlCustomField[]
  giftCard?: {
    number: string
    amount: string // numeric string
    isReload?: boolean
    clientID: string
    expiresAt: string
  }
  id: string
  note?: string
  priceSet?: boolean
  productID?: string
  promotions?: GqlLineItemPromotion[]
  quantity: string // numeric string
  registerID: string
  registerSequenceID: string
  sequence?: number
  sku?: string
  state?: GqlLineItemState
  taxComponents: Array<{ rateID: string; total: string }> // total as numeric string
  unitDiscount: string // numeric string
  unitLoyalty?: string // numeric string
  unitPrice: string // numeric string
  salespersonID?: string
  fulfilmentType?: GqlFulfilmentType
}

// Fulfilment details are grouped by fulfilment type
// For example, if there are 2 line items with different fulfilment types, there will be 2 fulfilment details
// If there are 2 line items with the same fulfilment type, there will be 1 fulfilment detail
export interface GqlFulfilmentDetail {
  note: string
  type: GqlFulfilmentType
  billingAddress?: GqlSaleAddress
  shippingAddress?: GqlSaleAddress
}

export interface SaleMutationData {
  customerID?: string
  id: string
  invoiceNumber?: string
  invoiceSequence?: number
  mutationID: string
  note?: string
  receipt?: {
    id: string
    recipientEmail: string
  }
  registerID: string
  resolveInvoiceNumber?: boolean
  saleDate?: string | null
  serviceFields?: ServiceFields
  shortCode?: string
  source?: string
  sourceID?: string
  legacyStatus: string
  userID: string
  payments: GqlPayment[]
  lineItems: GqlLineItem[]
  adjustments: GqlAdjustment[]
  customFields: GqlCustomField[]
  fulfilmentDetails: GqlFulfilmentDetail[]
  pickable?: boolean
  metadata?: SaleMetadata[]
}

export interface SaleMutationResponse {
  createSale: {
    invoiceNumber: string
    error?: SaleCreateError
    created?: boolean
  }
}

export interface SaleCreateError {
  code: SaleCreateErrorCode
  field?: SaleCreateErrorField
  message: string
}

export type PaymentLinksAvailabilityResult = {
  lspayIsPaymentLinksAvailable: boolean
}
export type PaymentLinksAvailabilityResponse = ApolloQueryResult<PaymentLinksAvailabilityResult>

export const createSaleMutation = gql`
  mutation CreateSaleMutation($input: CreateSaleInput!) {
    createSale(input: $input) {
      invoiceNumber
      error {
        code
        field
        message
      }
      created
    }
  }
`

export const checkPaymentLinksAvailability = gql`
  query CheckPaymentLinksAvailability($outletID: ID!) {
    lspayIsPaymentLinksAvailable(outletID: $outletID)
  }
`
